import { useRef, useState } from "react";
import type { LucideIcon } from "lucide-react";
import { Check, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useBreakpoint } from "../../../hooks/useBreakpoint";
import { useMotionSafe } from "../../../hooks/useMotionSafe";
import { cn } from "../cn";

export interface TabDefinition<Id extends string> {
    id: Id;
    label: string;
    icon: LucideIcon;
}

interface TabSwitcherProps<Id extends string> {
    tabs: TabDefinition<Id>[];
    activeTab: Id;
    onChange: (id: Id) => void;
    /** Distinguishes the sliding indicator when two switchers share a page. */
    layoutId?: string;
    /**
     * Accessible name for the tab list, e.g. "Patient type". Without one, a
     * screen reader announces "tab list" and nothing about what it switches.
     */
    label: string;
    /** id of the element the tabs control, wired up as aria-controls. */
    panelId?: string;
}

/**
 * Segmented tabs, collapsing to a dropdown on mobile.
 *
 * These carry real tab semantics — `role="tablist"`, `aria-selected`, roving
 * tabindex and arrow-key navigation. They previously rendered as a row of
 * buttons with `aria-pressed`, which announces as a set of unrelated toggles
 * and leaves the keyboard user tabbing through every option to reach the
 * content.
 *
 * Six pages had inlined their own copy of this pattern; they now share this one.
 */
export default function TabSwitcher<Id extends string>({
    tabs,
    activeTab,
    onChange,
    layoutId = "activeTabSwitcher",
    label,
    panelId,
}: TabSwitcherProps<Id>) {
    const { breakpoint } = useBreakpoint();
    const { reduced } = useMotionSafe();
    const isMobileView = breakpoint === "mobile";
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

    const active = tabs.find((tab) => tab.id === activeTab) ?? tabs[0];
    if (!active) return null;

    /** Arrow keys move between tabs and wrap, per the ARIA tabs pattern. */
    const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
        const offset =
            event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
        if (offset === 0) return;

        event.preventDefault();
        const next = tabs[(index + offset + tabs.length) % tabs.length];
        onChange(next.id);
        tabRefs.current[next.id]?.focus();
    };

    if (isMobileView) {
        return (
            <div className="relative z-30 mb-6 w-full">
                <div className="mx-auto max-w-sm px-1">
                    <div className="relative">
                        <button
                            type="button"
                            aria-haspopup="menu"
                            aria-expanded={isMobileMenuOpen}
                            aria-label={label}
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className={cn(
                                "flex h-12 w-full items-center justify-between rounded-lg border px-4",
                                "transition-colors duration-[--duration-fast] ease-[--ease-standard]",
                                isMobileMenuOpen
                                    ? "border-accent bg-surface-raised"
                                    : "border-border-subtle bg-surface-raised"
                            )}
                        >
                            <span className="flex items-center gap-3">
                                <active.icon aria-hidden="true" className="size-4 text-accent-text" />
                                <span className="text-sm font-semibold text-content-primary">
                                    {active.label}
                                </span>
                            </span>
                            <ChevronDown
                                aria-hidden="true"
                                className={cn(
                                    "size-5 text-content-secondary transition-transform duration-[--duration-base]",
                                    isMobileMenuOpen && "rotate-180"
                                )}
                            />
                        </button>

                        <AnimatePresence>
                            {isMobileMenuOpen && (
                                <>
                                    <div
                                        className="fixed inset-0 z-40 bg-transparent"
                                        onClick={() => setIsMobileMenuOpen(false)}
                                    />
                                    <motion.div
                                        role="menu"
                                        aria-label={label}
                                        initial={{ opacity: 0, y: reduced ? 0 : -8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: reduced ? 0 : -8 }}
                                        transition={{ duration: reduced ? 0.12 : 0.2 }}
                                        className="absolute left-0 right-0 z-50 mt-2 overflow-hidden rounded-lg border border-border-subtle bg-surface-overlay shadow-overlay"
                                    >
                                        <div className="space-y-1 p-1.5">
                                            {tabs.map((tab) => (
                                                <button
                                                    key={tab.id}
                                                    type="button"
                                                    role="menuitem"
                                                    onClick={() => {
                                                        onChange(tab.id);
                                                        setIsMobileMenuOpen(false);
                                                    }}
                                                    className={cn(
                                                        "flex min-h-11 w-full items-center justify-between rounded-md px-4 py-2.5",
                                                        "transition-colors duration-[--duration-fast]",
                                                        activeTab === tab.id
                                                            ? "bg-accent-tint font-semibold text-accent-on-tint"
                                                            : "font-medium text-content-secondary hover:bg-surface-sunken hover:text-content-primary"
                                                    )}
                                                >
                                                    <span className="flex items-center gap-3">
                                                        <tab.icon aria-hidden="true" className="size-4" />
                                                        <span className="text-sm">{tab.label}</span>
                                                    </span>
                                                    {activeTab === tab.id && (
                                                        <Check aria-hidden="true" className="size-4" />
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    </motion.div>
                                </>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="relative z-30 mb-6 w-full">
            <div className="no-scrollbar flex justify-start overflow-x-auto px-1 pb-1 sm:justify-center">
                <div
                    role="tablist"
                    aria-label={label}
                    /* Wraps rather than runs off the edge. Eight sections —
                       a centre's — are wider than the page they sit on, and
                       the row was simply clipped: the last tab half-drawn,
                       with a scrollbar deliberately hidden and so nothing to
                       say it was there. */
                    className="mx-auto inline-flex flex-wrap justify-center gap-y-1 whitespace-nowrap rounded-lg border border-border-subtle bg-surface-sunken p-1 sm:mx-0"
                >
                    {tabs.map((tab, index) => {
                        const isActive = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                type="button"
                                role="tab"
                                id={`${layoutId}-tab-${tab.id}`}
                                ref={(node) => {
                                    tabRefs.current[tab.id] = node;
                                }}
                                aria-selected={isActive}
                                aria-controls={panelId}
                                // Roving tabindex: one Tab press enters the group,
                                // arrow keys move within it.
                                tabIndex={isActive ? 0 : -1}
                                onKeyDown={(event) => handleKeyDown(event, index)}
                                onClick={() => onChange(tab.id)}
                                className={cn(
                                    "relative flex min-h-11 items-center gap-2 rounded-md px-4 text-sm font-semibold sm:px-5",
                                    "transition-colors duration-[--duration-fast] ease-[--ease-standard]",
                                    isActive
                                        ? "text-accent-on-tint"
                                        : "text-content-secondary hover:text-content-primary"
                                )}
                            >
                                {isActive && (
                                    <motion.span
                                        layoutId={reduced ? undefined : layoutId}
                                        aria-hidden="true"
                                        className="absolute inset-0 rounded-md bg-accent-tint"
                                        // Was a 0.6s bounce spring — over the 320ms
                                        // ceiling, and it overshot on every switch.
                                        transition={{ duration: 0.24, ease: [0.2, 0, 0, 1] }}
                                    />
                                )}
                                <tab.icon aria-hidden="true" className="relative z-10 size-4" />
                                <span className="relative z-10">{tab.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
